import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Card, Typography, Box } from "@mui/joy";
import { ImageUp } from "lucide-react";

interface ImageUploadDropzoneProps {
    onFilesAdded: (files: File[]) => void;
}

export function ImageUploadDropzone({ onFilesAdded }: ImageUploadDropzoneProps) {
    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            onFilesAdded(acceptedFiles);
        },
        [onFilesAdded]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/jpeg": [],
            "image/png": [],
        },
        multiple: true,
    });

    return (
        <div {...getRootProps()}>
            <Card
                sx={{
                    p: 0,
                    width: 150,
                    height: 150,
                    border: "1.5px dashed",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    bgcolor: isDragActive ? "action.hover" : "transparent",
                    position: "relative",
                }}
                aria-label="Upload image dropzone"
            >
                {/* Make input clickable but visually hidden */}
                <input
                    {...getInputProps()}
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        top: 0,
                        left: 0,
                        opacity: 0,
                        cursor: "pointer",
                    }}
                />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        pointerEvents: "none", // Only for the content, not the card itself
                    }}
                >
                    <ImageUp size={20} />
                    <Typography sx={{ mt: 1 }} level="body-sm">
                        {isDragActive ? "Drop the image here..." : "Upload an Image"}
                    </Typography>
                    <Typography level="body-xs">Supported formats: JPG or PNG</Typography>
                </Box>
            </Card>
        </div>
    );
}
